/**
 * The Propagate dialog's root check (S8 Task 8.11, finding S8-07): the root directory's ETag is read when the dialog
 * opens (`hns-resolve-path`) and sent as `expectedRootEtag`; main re-checks it right before the first batch
 * (`TargetChanged`, nothing sent). The read is cancelled when the dialog closes; `reload()` reads it again.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { HnsPathRef, StorageOperationError } from '../../../../../types/azureStorage';
import { cancelStorageRequest, newStorageRequestId } from '../../services/azureStorageApi';
import { hnsResolvePath } from '../hnsApi';

export type RootVersion = { etag?: string; error?: StorageOperationError; loading: boolean };

export function useHnsRootVersion(connectionId: string, root: HnsPathRef) {
  const [state, setState] = useState<RootVersion>({ loading: true });
  const [attempt, setAttempt] = useState(0);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    const requestId = newStorageRequestId();
    setState({ loading: true });
    void hnsResolvePath({ connectionId, target: { fileSystem: root.fileSystem, path: root.path }, requestId }).then((answer) => {
      if (!live.current) return;
      if (answer.ok === false) { setState({ loading: false, error: answer.error }); return; }
      if (!answer.value.resource.isDirectory) {
        setState({ loading: false, error: { category: 'conflict', code: 'TargetChanged', retryable: false, message: `${root.path ? `${root.fileSystem}/${root.path}` : root.fileSystem} is no longer a directory.` } });
        return;
      }
      setState({ loading: false, etag: answer.value.etag });
    });
    return () => { live.current = false; void cancelStorageRequest(requestId); };
  }, [connectionId, root.fileSystem, root.path, attempt]);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, reload };
}
